import { useInfiniteQuery } from '@tanstack/react-query';

import { api } from './client';
import type { CatalogueEntry, CatalogueRow, DiscoveryKind } from './types';

interface CatalogueRequest {
  row: CatalogueRow;
  kind: DiscoveryKind;
  query?: string;
  genreId?: number;
  enabled?: boolean;
}

interface CatalogueState {
  entries: CatalogueEntry[];
  error: Error | undefined;
  /// True only while there is nothing on screen yet. Fetching the next page is not this.
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

/// The catalogue as one endless list rather than as pages.
///
/// Nobody browsing for something to watch wants to click "next". The pages are what the
/// catalogue answers with, and they are stitched back together here so that the screen above
/// only ever sees a list that grows.
export function useCatalogue({
  row,
  kind,
  query,
  genreId,
  enabled = true,
}: CatalogueRequest): CatalogueState {
  const catalogue = useInfiniteQuery({
    queryKey: ['discovery', 'catalogue', row, kind, query ?? '', genreId ?? null],
    queryFn: ({ pageParam }) => api.catalogue({ row, kind, query, genreId, page: pageParam }),
    initialPageParam: 1,

    // The server says whether asking again is worth it, because only it knows whether the
    // catalogue ran out or merely answered short.
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    enabled,

    // The catalogue changes by the day, not by the minute, and re-reading it on every visit
    // would throw away a scroll somebody spent a while building.
    staleTime: 5 * 60 * 1000,
  });

  return {
    entries: (catalogue.data?.pages ?? []).flatMap((page) => page.items),
    error: (catalogue.error as Error | null) ?? undefined,
    loading: catalogue.isPending && catalogue.fetchStatus !== 'idle',
    loadingMore: catalogue.isFetchingNextPage,
    hasMore: catalogue.hasNextPage,
    loadMore: () => {
      if (catalogue.hasNextPage && !catalogue.isFetchingNextPage) {
        void catalogue.fetchNextPage();
      }
    },
    reload: () => void catalogue.refetch(),
  };
}
